import { gql, useQuery } from '@apollo/client';
import React, { Dispatch, forwardRef, SetStateAction, useEffect, useImperativeHandle, useState } from 'react';
import { camelCase, range, toUpper } from 'lodash';
import { Space, Switch } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import RemoveRecord from '../components/RemoveRecord';
import { SupportedEntitySearchTypes } from '../../search_old/types';
import { useTableColumns } from '../../../components/entitiesSearch/results/useTableColumns';
import EntityTable from '../../../components/entitiesSearch/results/EntityTable';
import { usePersonTableQuickActions } from '../../person/hooks/usePersonTableQuickActions';
import { EntityTypeEnum } from '../../../../gql/typings';
import { useLocalization } from '../../../util/useLocalization';
import { Locale } from '../../../../localization/LocalizationKeys';
import { usePermission } from '../../../permission/usePermission';

type ListDetailsRecordsTableProps = {
  listId: number;
  entityType: SupportedEntitySearchTypes;
  includeUnplacedRecords?: [boolean, Dispatch<SetStateAction<boolean>>];
};

export type ListDetailsRecordsTableRef = {
  refetch: () => void;
};

const ListDetailsRecordsTable = forwardRef<ListDetailsRecordsTableRef, ListDetailsRecordsTableProps>(({
  listId,
  entityType,
  includeUnplacedRecords,
}, ref) => {
  const localization = useLocalization();
  const { read: canSearchUnplaced } = usePermission('SEARCH_UNPLACED');
  const localIncludeUnplacedRecords = useState(false);
  const [includeUnplaced, setIncludeUnplaced] = includeUnplacedRecords ?? localIncludeUnplacedRecords;
  const renderUnplacedToggle = entityType === EntityTypeEnum.PERSON && canSearchUnplaced;
  const {
    tableSelection,
    entitiesSearchProps,
    HiddenRender,
  } = usePersonTableQuickActions(undefined, {
    listId,
    includeUnplacedRecords: [includeUnplaced, setIncludeUnplaced],
  });
  const { query, sorting, loading: fieldsLoading } = useTableColumns(entityType);
  const fetchSize = {
    limit: tableSelection.tableProps.perPageState[0],
    offset: (tableSelection.tableProps.pageState[0] - 1) * tableSelection.tableProps.perPageState[0],
  };
  const ordering = sorting?.map(s => s.currentEnum);
  const usePersonRootQuery = entityType === EntityTypeEnum.PERSON && includeUnplaced;
  const variables = usePersonRootQuery
    ? {
      listId,
      criteria: {
        listId,
        includeUnplaced,
        ordering,
        fetchSize,
      },
    }
    : {
      listId,
      ordering,
      fetchSize,
    };

  const { data, loading, refetch } = useQuery(
    buildDataQuery(entityType, query, usePersonRootQuery), {
      fetchPolicy: 'network-only',
      skip: fieldsLoading,
      variables,
    },
  );

  useImperativeHandle(ref, () => ({
    refetch,
  }), [refetch]);

  const isDynamic = entityType === EntityTypeEnum.PERSON
    ? data?.list?.listType.code === 'DYN'
    : data?.list?.listType.code === 'DYN';
  const connection = usePersonRootQuery
    ? data?.connection
    : data?.list?.connection;

  useEffect(() => {
    tableSelection.tableProps.onVisibleKeysChange?.(connection?.nodes?.map((k: { id: number }) => k.id) ?? []);
    if (tableSelection.tableProps.onDataChange && connection) tableSelection.tableProps.onDataChange(connection);
  }, [connection, tableSelection.tableProps]);

  let records = connection?.nodes ?? [];
  const totalCount = connection?.totalCount ?? 0;
  if (records.length === 0 && loading) {
    const amount = totalCount - (tableSelection.tableProps.pageState[0] * tableSelection.tableProps.perPageState[0]);
    if (amount > 0) {
      records = range(-amount).map(id => ({ id }));
    }
  }

  return (
    <div style={{ width: '100%' }} className="list-details-records-table">
      <HiddenRender />
      {renderUnplacedToggle && (
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Switch
              checkedChildren={<CheckOutlined />}
              checked={includeUnplaced}
              onChange={setIncludeUnplaced}
              unCheckedChildren={<CloseOutlined />}
            />
            {localization.formatMessage(Locale.Command.Include_unplaced)}
          </Space>
        </div>
      )}
      <EntityTable
        entityType={entityType}
        tableSelection={tableSelection}
        actionsToTake={entityType === 'PERSON' ? entitiesSearchProps.actions : undefined}
        data={{
          nodes: records,
          totalCount: connection?.totalCount,
          loading: loading || fieldsLoading,
        }}
        additionalColumns={[
          !isDynamic && !loading && {
            title: '',
            key: 'remove',
            render: (_: string, record: { id: number }) => (
              <RemoveRecord
                render={!isDynamic}
                listId={listId}
                recordId={record.id}
              />
            ),
          },
        ]}
      />
    </div>
  );
});
// @ts-ignore
// noinspection GraphQLSchemaValidation
export const buildDataQuery = (entityType: EntityTypeEnum, fields: string|null, usePersonRootQuery = false) => (
  usePersonRootQuery
    ? gql`
      query ListDetailsPersonTableQuery($listId: Int!, $criteria: PersonCriteria) {
        list(id: $listId) {
          id
          entityTypeEnum
          listType { code }
        }
        ${buildPersonListQuery(fields)}
      }
    `
    : gql`
      query ListDetailsTableQuery($listId: Int!, $fetchSize: FetchSize, $ordering: [String!]) {
        list(id: $listId) {
          id
          entityTypeEnum
          listType { code }
          ${buildAffiliationQuery(entityType, fields)}
        }
      }
    `
);

const buildPersonListQuery = (fields: string|null) => `
  connection: persons(criteria: $criteria) {
    hash
    totalCount
    ${fields ? `nodes {
      ${fields}
    }` : ''}
  }
`;

// TODO: Nasty stuff. Try to figure out some better and typescript solution to this.
const buildAffiliationQuery = (entityType: EntityTypeEnum, fields: string|null) => `
  connection: affiliations(fetchSize: $fetchSize, ordering: $ordering) {
    ... on ${camelCase(entityType).replace(/^(.)/, toUpper)}Connection {
      hash
      totalCount
      ${fields ? `nodes {
        ${fields}
      }` : ''}
    }
  }
`;

export default ListDetailsRecordsTable;
